import { UserDB } from 'lib/db';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getValidTypeSource } from 'views/Conversation/Sider/ToDoBundle/helper';
import { TypeSource } from 'views/Conversation/Sider/ToDoBundle/NewTodo';

interface UseTickTypeSourceProps {
  taskId: number;
  remoteTypeSource: TypeSource;
  platform: General.PlatformUpperCase;
}

export type TickTypeSourceItem = TypeSource[number] & {
  localEnable: boolean;
  sortIndex?: number;
};

export const useTickTypeSource = (props: UseTickTypeSourceProps) => {
  const { taskId, remoteTypeSource, platform } = props;
  const [localTypeSource, setLocalTypeSource] = useState<TickTypeSourceItem[]>([]);

  /** 服务端可用数据 */
  const remoteValidTypeSource = useMemo(() => {
    return getValidTypeSource(remoteTypeSource, platform);
  }, [remoteTypeSource, platform]);

  const refresh = useCallback(() => {
    let cancelled = false;

    const fallbackTypeSource = remoteValidTypeSource.map((item) => ({
      ...item,
      localEnable: true,
    }));

    const db = UserDB.instance;
    if (!db) {
      setLocalTypeSource(fallbackTypeSource);
      return () => {
        cancelled = true;
      };
    }

    db.todoTypeSource
      .where('taskId')
      .equals(taskId)
      .toArray()
      .then((cacheTypeSource) => {
        if (cancelled) return;

        const cacheMap = new Map(cacheTypeSource.map((item) => [item.type, item]));
        const mergedTypeSource = remoteValidTypeSource
          .map((item, index) => {
            const cacheItem = cacheMap.get(item.type);
            return {
              ...item,
              localEnable: cacheItem?.localEnable ?? true,
              sortIndex: cacheItem?.sortIndex ?? index,
            };
          })
          .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0));

        setLocalTypeSource(mergedTypeSource);
      })
      .catch(() => {
        if (!cancelled) {
          setLocalTypeSource(fallbackTypeSource);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [taskId, remoteValidTypeSource]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return {
    remoteValidTypeSource,
    localTypeSource,
    refresh,
  };
};
